<template>
  <div class="dictionary-container">
    <!-- 左侧树形菜单 -->
    <div class="left-panel">
      <div class="panel-header">
        <span class="panel-title">字典菜单</span>
        <el-button type="primary" size="small" :icon="Plus" circle @click="handleAddMenu(0)"></el-button>
      </div>
      <div class="panel-body">
        <el-tree
          :data="treeData"
          :props="defaultProps"
          node-key="id"
          default-expand-all
          :expand-on-click-node="false"
          @node-click="handleNodeClick"
          highlight-current
        >
          <template #default="{ node, data }">
            <span class="custom-tree-node">
              <span class="node-label">{{ node.label }}</span>
              <span class="node-actions">
                <!-- 添加子级 -->
                <el-button
                  type="text"
                  size="small"
                  :icon="Plus"
                  @click.stop="handleAddMenu(data.id)"
                  title="添加子级"
                ></el-button>
                <!-- 修改 -->
                <el-button
                  type="text"
                  size="small"
                  :icon="Edit"
                  @click.stop="handleEditMenu(data)"
                  title="修改"
                ></el-button>
                <!-- 删除 -->
                <el-button
                  type="text"
                  size="small"
                  style="color: var(--el-color-danger);"
                  :icon="Delete"
                  @click.stop="handleDeleteMenu(data)"
                  title="删除"
                ></el-button>
              </span>
            </span>
          </template>
        </el-tree>
      </div>
    </div>

    <!-- 右侧字典数据表格 -->
    <div class="right-panel">
      <div v-if="selectedNode" class="data-wrapper">
        <div class="panel-header">
          <div class="node-info">
            <span class="current-node-name">{{ selectedNode.name }}</span>
            <el-tag v-if="selectedNode.code" size="small" type="info" class="code-tag">{{ selectedNode.code }}</el-tag>
          </div>
          <el-button type="primary" size="default" :icon="Plus" @click="handleAddData">
            新增字典值
          </el-button>
        </div>
        <div class="panel-body">
          <el-table :data="tableData" border style="width: 100%" height="calc(100vh - 200px)">
            <el-table-column type="index" label="序号" width="80" align="center" />
            <el-table-column prop="dict_label" label="字典标签(展示值)" align="center" />
            <el-table-column prop="dict_value" label="字典键值(实际值)" align="center" />
            <el-table-column prop="sort_order" label="排序" width="100" align="center" />
            <el-table-column prop="status" label="状态" width="120" align="center">
              <template #default="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'danger'">
                  {{ row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" align="center" show-overflow-tooltip />
            <el-table-column label="操作" width="150" align="center">
              <template #default="{ row }">
                <el-button type="text" size="small" @click="handleEditData(row)">编辑</el-button>
                <el-button type="text" size="small" style="color: var(--el-color-danger);" @click="handleDeleteData(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div v-else class="empty-state">
        <el-empty description="请在左侧选择字典分类或字典项" />
      </div>
    </div>

    <!-- 字典菜单 (新增/修改) 弹窗 -->
    <el-dialog
      v-model="menuDialogVisible"
      :title="menuForm.id ? '修改字典菜单' : '新增字典菜单'"
      width="500px"
      align-center
    >
      <el-form :model="menuForm" ref="menuFormRef" :rules="menuRules" label-width="90px">
        <el-form-item label="字典名称" prop="name">
          <el-input v-model="menuForm.name" placeholder="请输入字典分类或项的名称" />
        </el-form-item>
        <el-form-item label="字典编码" prop="code">
          <el-input v-model="menuForm.code" placeholder="请输入唯一编码（非必填）" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input type="textarea" :rows="3" v-model="menuForm.remark" placeholder="请输入备注" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="menuDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitMenuForm">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 字典值 (新增/修改) 弹窗 -->
    <el-dialog
      v-model="dataDialogVisible"
      :title="dataForm.id ? '修改字典值' : '新增字典值'"
      width="500px"
      align-center
    >
      <el-form :model="dataForm" ref="dataFormRef" :rules="dataRules" label-width="auto">
        <el-form-item label="字典标签(展示值)" prop="dict_label">
          <el-input v-model="dataForm.dict_label" placeholder="例如：启用" />
        </el-form-item>
        <el-form-item label="字典键值(实际值)" prop="dict_value">
          <el-input v-model="dataForm.dict_value" placeholder="例如：1" />
        </el-form-item>
        <el-form-item label="显示排序" prop="sort_order">
          <el-input-number v-model="dataForm.sort_order" :min="0" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="dataForm.status" style="width: 100%;">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input type="textarea" :rows="3" v-model="dataForm.remark" placeholder="请输入备注" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dataDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitDataForm">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Plus, Edit, Delete } from "@element-plus/icons-vue";
import {
  DictMenuList,
  DictMenuSave,
  DictMenuDelete,
  DictDataList,
  DictDataSave,
  DictDataDelete
} from "@/api/dictionary.js";

// 树属性定义
const defaultProps = {
  children: "children",
  label: "name"
};

// 状态定义
const rawMenuList = ref([]);
const selectedNode = ref(null);
const tableData = ref([]);

// 弹窗状态与表单定义
const menuDialogVisible = ref(false);
const menuFormRef = ref(null);
const menuForm = reactive({
  id: 0,
  name: "",
  code: "",
  parent_id: 0,
  remark: ""
});

const dataDialogVisible = ref(false);
const dataFormRef = ref(null);
const dataForm = reactive({
  id: 0,
  menu_id: 0,
  dict_label: "",
  dict_value: "",
  sort_order: 0,
  status: 1,
  remark: ""
});

// 表单校验规则
const menuRules = {
  name: [{ required: true, message: "字典分类或菜单项名称不能为空", trigger: "blur" }]
};

const dataRules = {
  dict_label: [{ required: true, message: "字典展示标签不能为空", trigger: "blur" }],
  dict_value: [{ required: true, message: "实际键值不能为空", trigger: "blur" }]
};

// 计算树形结构数据
const treeData = computed(() => {
  const map = {};
  rawMenuList.value.forEach(item => {
    map[item.id] = { ...item, children: [] };
  });
  const tree = [];
  rawMenuList.value.forEach(item => {
    const node = map[item.id];
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(node);
    } else {
      tree.push(node);
    }
  });
  return tree;
});

// 生命周期
onMounted(() => {
  getMenuList();
});

// 获取树形字典菜单数据
const getMenuList = async () => {
  try {
    const res = await DictMenuList();
    if (res.code === 200) {
      rawMenuList.value = res.data || [];
    } else {
      ElMessage.error(res.msg || "获取字典菜单失败");
    }
  } catch (error) {
    ElMessage.error("获取字典菜单失败，请重试");
  }
};

// 选择树节点
const handleNodeClick = (data) => {
  selectedNode.value = data;
  getDataList();
};

// 获取右侧字典数值列表
const getDataList = async () => {
  if (!selectedNode.value) return;
  try {
    const res = await DictDataList({ menu_id: selectedNode.value.id });
    if (res.code === 200) {
      tableData.value = res.data || [];
    } else {
      ElMessage.error(res.msg || "获取字典数值失败");
    }
  } catch (error) {
    ElMessage.error("获取字典数值失败");
  }
};

// ================== 字典菜单节点 CRUD ==================

// 点击新增菜单
const handleAddMenu = (parentId) => {
  Object.assign(menuForm, {
    id: 0,
    name: "",
    code: "",
    parent_id: parentId,
    remark: ""
  });
  menuDialogVisible.value = true;
};

// 点击修改菜单
const handleEditMenu = (data) => {
  Object.assign(menuForm, {
    id: data.id,
    name: data.name,
    code: data.code,
    parent_id: data.parent_id,
    remark: data.remark
  });
  menuDialogVisible.value = true;
};

// 提交菜单表单
const submitMenuForm = async () => {
  if (!menuFormRef.value) return;
  await menuFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = await DictMenuSave({ ...menuForm });
        if (res.code === 200) {
          ElMessage.success("保存成功");
          menuDialogVisible.value = false;
          getMenuList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (error) {
        ElMessage.error("保存失败，请重试");
      }
    }
  });
};

// 删除字典分类节点
const handleDeleteMenu = (data) => {
  ElMessageBox.confirm(`确认要删除字典项“${data.name}”吗？此操作将一并删除其下的全部数值项。`, "删除提醒", {
    confirmButtonText: "确认",
    cancelButtonText: "取消",
    type: "warning"
  }).then(async () => {
    try {
      const res = await DictMenuDelete({ id: data.id });
      if (res.code === 200) {
        ElMessage.success("删除成功");
        if (selectedNode.value && selectedNode.value.id === data.id) {
          selectedNode.value = null;
          tableData.value = [];
        }
        getMenuList();
      } else {
        ElMessage.error(res.msg || "删除失败");
      }
    } catch (error) {
      ElMessage.error("删除失败");
    }
  }).catch(() => {});
};

// ================== 字典键值数据 CRUD ==================

// 新增字典值
const handleAddData = () => {
  if (!selectedNode.value) return;
  Object.assign(dataForm, {
    id: 0,
    menu_id: selectedNode.value.id,
    dict_label: "",
    dict_value: "",
    sort_order: 0,
    status: 1,
    remark: ""
  });
  dataDialogVisible.value = true;
};

// 编辑字典值
const handleEditData = (row) => {
  Object.assign(dataForm, {
    id: row.id,
    menu_id: row.menu_id,
    dict_label: row.dict_label,
    dict_value: row.dict_value,
    sort_order: row.sort_order,
    status: row.status,
    remark: row.remark
  });
  dataDialogVisible.value = true;
};

// 提交字典值表单
const submitDataForm = async () => {
  if (!dataFormRef.value) return;
  await dataFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = await DictDataSave({ ...dataForm });
        if (res.code === 200) {
          ElMessage.success("保存成功");
          dataDialogVisible.value = false;
          getDataList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (error) {
        ElMessage.error("保存失败");
      }
    }
  });
};

// 删除字典值
const handleDeleteData = (row) => {
  ElMessageBox.confirm(`确认要删除数值“${row.dict_label}”吗？`, "删除提醒", {
    confirmButtonText: "确认",
    cancelButtonText: "取消",
    type: "warning"
  }).then(async () => {
    try {
      const res = await DictDataDelete({ id: row.id });
      if (res.code === 200) {
        ElMessage.success("删除成功");
        getDataList();
      } else {
        ElMessage.error(res.msg || "删除失败");
      }
    } catch (error) {
      ElMessage.error("删除失败");
    }
  }).catch(() => {});
};
</script>

<style lang="less" scoped>
.dictionary-container {
  display: flex;
  height: calc(100vh - 100px);
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
  background-color: #f4f5fa;
}

.left-panel {
  width: 300px;
  background-color: #fff;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  margin-right: 15px;
}

.right-panel {
  flex: 1;
  background-color: #fff;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  overflow: hidden;
}

.panel-header {
  height: 55px;
  padding: 0 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.panel-body {
  flex: 1;
  padding: 15px;
  overflow: auto;
  box-sizing: border-box;
}

/* 树节点自定义悬浮展示操作 */
.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
  
  .node-actions {
    display: none;
    align-items: center;
    gap: 4px;
  }
}

.custom-tree-node:hover {
  .node-actions {
    display: inline-flex;
  }
}

.node-label {
  color: #555;
}

/* 右边栏头部的选中节点展示 */
.node-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-node-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--el-color-primary);
}

.code-tag {
  font-family: monospace;
}

.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.data-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
